import React, { useState, useEffect, useContext, useMemo } from 'react';
import * as api from 'app/shared/api';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Row,
  Col,
  Input,
  Select,
  Modal,
  Form,
  Checkbox,
  DatePicker,
  Button,
  Space,
  Switch,
  message,
} from 'antd';
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { AccommodationProduct } from 'app/shared/model/Accommodation';
import DateHelper from 'app/helpers/dateHelper';
import { DivisionId } from 'app/shared/division/getDivisionId';
import {
  filterData,
  getAccommodationProductById,
  listAccommodationProductsData,
  resetAccommodationModal,
  setFormErrors
} from 'app/pages/Products/actions';
import {
  FilterValueType,
  Product,
  ProductFilter,
  ProductKey,
} from 'app/shared/model/ProductBase';
import filterHelper from 'app/helpers/filterHelper';
import { getAccommodationProductsData, getFilteredData } from '../selectors';
import "../Page.css";
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import TablePagination from 'app/shared/components/Table/TablePagination';
import 'app/shared/components/Table/Table.css';
import AddEditAccommodationForm from './AddEditAccommodationForm';
import { ColumnType } from 'antd/es/table';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { ProductStatusFilterValue } from './productStatusFilterUtils';
import { buildGridFilteredList } from './productGridFilterPipeline';
import { useAutoOpenProductModal } from '../hooks/useAutoOpenProductModal';

type EditableColumn<T> = ColumnType<T> & {
  editable?: boolean;
  inputType?: 'text' | 'select' | 'checkbox' | 'date';
  options?: { label: string; value: any }[];
};

export interface AccommodationRoom {
  id: number;
  ProductName: string;
  legacyCode?: string;
  division?: string;
  roomGradeId?: number;
  bathroomTypeId?: number;
  boardBasisId?: number;
  roomTypeId?: number;
  productCategoryId?: number;
  accountCategoryId?: number;
  roomGradeText?: string;
  bathroomTypeText?: string;
  boardBasisText?: string;
  roomTypeText?: string;
  productCategoryText?: string;
  accountCategoryText?: string;
  occupyRoom?: boolean;
  decommissionDate?: Dayjs;
  active?: boolean;
  entityUpdate?: boolean;
  [key: string]: string | number | boolean | Dayjs | undefined;
}

export interface AccommodationGridProps {}

function AccommodationGrid() {
  const dispatch = useDispatch<any>();
  const filteredData = useSelector(getFilteredData);
  const accommodationProducts = useSelector(getAccommodationProductsData);

  const onFilterData = (fd: Product[] | undefined) => dispatch(filterData(fd));
  const onAccommodationData = (list: AccommodationProduct[]) =>
    dispatch(listAccommodationProductsData(list));
  const onSelectedAccommodationData = (ap: AccommodationProduct | undefined | void) =>
    dispatch(getAccommodationProductById(ap as AccommodationProduct | undefined));
  const onOpenNewAccommodation = () => {
    dispatch(setFormErrors([]));
    dispatch(resetAccommodationModal());
  };
  const onCancel = () => {
    dispatch(setFormErrors([]));
    dispatch(resetAccommodationModal());
  };

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ProductFilter[]>([]);
  const [statusFilter, setStatusFilter] = useState<ProductStatusFilterValue>('all');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchFilteredData, setSearchFilteredData] = useState<AccommodationProduct[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Preserve backend ordering by default; user can still sort by clicking columns.
  const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting(null);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalAccId, setModalAccId] = useState<number | null>(null);
  const [modalAccName, setModalAccName] = useState<string | null>(null);

  const [detailData, setDetailData] = useState<Record<number, AccommodationRoom[]>>({});
  const [pendingRoomIds, setPendingRoomIds] = useState<Set<number>>(new Set());

  const [roomGradeOptions, setRoomGradeOptions] = useState<{ label: string; value: number }[]>([]);
  const [bathroomTypeOptions, setBathroomTypeOptions] = useState<{ label: string; value: number }[]>([]);
  const [boardBasisOptions, setBoardBasisOptions] = useState<{ label: string; value: number }[]>([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState<{ label: string; value: number }[]>([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState<{ label: string; value: number }[]>([]);
  const [accountCategoryOptions, setAccountCategoryOptions] = useState<{ label: string; value: number }[]>([]);
  const [expandAll, setExpandAll] = useState(false);
  const [savingRoom, setSavingRoom] = useState(false);

  const callFirstAvailable = async <T,>(calls: Array<(() => Promise<T>) | undefined>, fallback: T) => {
    for (const fn of calls) {
      try {
        if (typeof fn === 'function') {
          const result = await fn();
          if (result != null) return result;
        }
      } catch {
      }
    }
    return fallback;
  };

  const refreshGridEvent = async () => {
    const list = await api.getAccommodationProducts(DivisionId());

    // Keep the base list in sync so search/filter uses latest data.
    onAccommodationData(list ?? []);

    publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);

    // If we have pending rooms, re-fetch expanded/loaded room lists so we can
    // clear pending state once publishing completes and backend data updates.
    if (pendingRoomIds.size > 0) {
      const parentIdsToRefresh = new Set<number>();
      expandedRowKeys.forEach((k) => {
        const n = Number(k);
        if (Number.isFinite(n) && n > 0) parentIdsToRefresh.add(n);
      });
      Object.keys(detailData ?? {}).forEach((k) => {
        const n = Number(k);
        if (Number.isFinite(n) && n > 0) parentIdsToRefresh.add(n);
      });
      parentIdsToRefresh.forEach((id) => {
        void fetchRooms(id);
      });
    }
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.AccommodationEventPublished, PublishedEventType.AccommodationRoomEventPublished], refreshGridEvent, DivisionId());

  useEffect(() => {
    let mounted = true;

    const mapOptions = <T,>(rows: T[], labelKey: keyof T, valueKey: keyof T) =>
      (rows || [])
        .filter((r: any) => Number(r?.[valueKey]) > 0)
        .map((r: any) => ({ label: String(r?.[labelKey] ?? ''), value: Number(r?.[valueKey]) }));

    (async () => {
      try {
        const [
          grades,
          baths,
          boards,
          types,
          prodCats,
          accCats,
        ] = await Promise.all([
          api.getAllRoomGrades(),
          api.getAllBathroomTypes(),
          api.getAllBoardBasis(),
          api.getAllRoomTypes(),
          api.getAllProductCategories(),
          api.getAllAccountCategories(),
        ]);
        if (!mounted) return;
        setRoomGradeOptions(mapOptions(grades ?? [], 'name', 'id'));
        setBathroomTypeOptions(mapOptions(baths ?? [], 'name', 'id'));
        setBoardBasisOptions(mapOptions(boards ?? [], 'name', 'id'));
        setRoomTypeOptions(mapOptions(types ?? [], 'name', 'id'));
        setProductCategoryOptions(mapOptions(prodCats ?? [], 'name', 'id'));
        setAccountCategoryOptions(mapOptions(accCats ?? [], 'name', 'id'));
      } catch (e) {
        message.error('Failed to load reference data');
      }
    })();

    return () => { mounted = false; };
  }, []);

  const toMap = (opts: { label: string; value: number }[]) => new Map(opts.map(o => [o.value, o.label]));
  const roomGradeMap = useMemo(() => toMap(roomGradeOptions), [roomGradeOptions]);
  const bathroomTypeMap = useMemo(() => toMap(bathroomTypeOptions), [bathroomTypeOptions]);
  const boardBasisMap = useMemo(() => toMap(boardBasisOptions), [boardBasisOptions]);
  const roomTypeMap = useMemo(() => toMap(roomTypeOptions), [roomTypeOptions]);
  const productCategoryMap = useMemo(() => toMap(productCategoryOptions), [productCategoryOptions]);
  const accountCategoryMap = useMemo(() => toMap(accountCategoryOptions), [accountCategoryOptions]);

  const getAccommodationProduct = (id: number, name: string) => {
    api.getAccommodationById(id.toString()).then((accom) => {
      if (!accom) return;

      onSelectedAccommodationData(accom);
      setModalAccId(Number(accom.id));
      setModalAccName(name || accom.name);
      setModalOpen(true);
    });
  };

  const openNewAccommodation = () => {
    onOpenNewAccommodation();
    const blankAccommodation: AccommodationProduct = {
      id: 0,
      name: '',
      type: '',
      typeId: 0,
      minStay: 0,
      decommissionDate: undefined,
      minAge: 0,
      maxAge: null,
      isCommitted: false,
      isNonCommitted: false,
      lastModifiedDate: new Date(),
      lastUpdatedBy: '',
      division: 0,
      divisionText: '',
      entityUpdate: false,
      active: true,
        excludedBookingSystemIds: [],
    };
    onSelectedAccommodationData(blankAccommodation);

    setModalAccId(null);
    setModalAccName('New Accommodation');
    setModalOpen(true);
  };
  const [addRoomModalOpen, setAddRoomModalOpen] = useState(false);
  const [currentParentId, setCurrentParentId] = useState<number | null>(null);
  const [newRoom, setNewRoom] = useState<Partial<AccommodationRoom>>({});
  const [isEditRoom, setIsEditRoom] = useState(false);

  const productList = accommodationProducts ?? [];

  const publishFilteredData = React.useCallback((
    nextFilters = filters,
    nextShowFilters = showFilters,
    nextStatus = statusFilter,
    nextSearchActive = isSearchActive,
    nextSearchData = searchFilteredData,
  ) => {
    onFilterData(buildGridFilteredList(productList, {
      showFilters: nextShowFilters,
      filters: nextFilters,
      isSearchActive: nextSearchActive,
      searchFilteredData: nextSearchData,
      statusFilter: nextStatus,
    }));
  }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, productList, onFilterData]);

  const handleClearFilters = () => {
    setFilters([]);
    setCurrentPage(1);
    publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
  };

  const handleFilterChange = (
    value: any,
    field: keyof ProductKey,
    valueType: FilterValueType = FilterValueType.primaryValue
  ) => {
    const newFilters = filterHelper.getFilters(filters, value, field, valueType);
    setFilters(newFilters);
    publishFilteredData(newFilters);
    setCurrentPage(1);
  };

  const getUniqueTypeFilter = (list: AccommodationProduct[]) => {
    const typeData = list.map(d => ({ id: d.type, name: d.type }));
    const unique = typeData.filter((v,i,self) => self.findIndex(x => x.id === v.id) === i);
    return unique.map(v => ({ text: v.name, value: v.id }));
  };
  const getUniqueMinStayFilter = (list: AccommodationProduct[]) => {
    const minStayData = list.map(d => ({ id: d.minStay, name: `${d.minStay} weeks` }));
    const unique = minStayData.filter((v,i,self) => self.findIndex(x => x.id === v.id) === i);
    return unique.map(v => ({ text: v.name, value: v.id }));
  };

  const toNum = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  const toDay = (v: any) => (v ? dayjs(v) : undefined);

  const mapApiRoomToAccommodationRoom = (r: any): AccommodationRoom => {
    const id = Number(r.productId ?? r.ProductId ?? r.id ?? 0);
    const productName = String(r.ProductName ?? r.productName ?? r.Name ?? r.name ?? "").trim();

    return {
      ...r,
      id,
      ProductName: productName,
      roomGradeId: toNum(r.RoomGradeId ?? r.roomGradeId),
      bathroomTypeId: toNum(r.BathroomTypeId ?? r.bathroomTypeId),
      boardBasisId: toNum(r.BoardBasisId ?? r.boardBasisId ?? r.BoardTypeId ?? r.boardTypeId),
      roomTypeId: toNum(r.RoomTypeId ?? r.roomTypeId),
      productCategoryId: toNum(r.ProductCategoryId ?? r.productCategoryId),
      accountCategoryId: toNum(r.AccountCategoryId ?? r.accountCategoryId),
      decommissionDate: toDay(r.OfferingsClosureDate ?? r.decommissionDate),
      entityUpdate: Boolean(r.entityUpdate),
      active: r.active ?? r.Active ?? true,
      occupyRoom: Boolean(r.occupyRoom ?? r.OccupyRoom),
    };
  };

  const fetchRooms = async (parentId: number) => {
    try {
      const res: any = await api.getAccommodationRoomsByAccommodationId(parentId.toString());
      const rooms: any[] = Array.isArray(res) ? res : (res?.items || res?.data || []);
      const mapped: AccommodationRoom[] = (rooms || []).map(mapApiRoomToAccommodationRoom);

      setDetailData((prev) => {
        const existing = prev[Number(parentId)] ?? [];
        
        const pending = (existing ?? [])
          .filter((x) => x && (x as any).entityUpdate === true)
          .filter((x) => !mapped.some((m) => Number(m?.id) === Number((x as any)?.id)));

        return { ...prev, [parentId]: [...mapped, ...pending] };
      });

      if (mapped.length > 0) {
        setPendingRoomIds((prev) => {
          if (!prev.size) return prev;
          const next = new Set(prev);
          mapped.forEach((r) => {
            const id = Number((r as any)?.id ?? 0);
            if (!Number.isFinite(id) || id <= 0) return;
            if ((r as any)?.entityUpdate !== true) {
              next.delete(id);
            }
          });
          return next;
        });
      }
    } catch (e) {
      console.error('Failed to load rooms', e);
      message.error('Failed to load rooms.');
    }
  };

  const upsertRoomInState = React.useCallback(
    (parentId: number, room: AccommodationRoom) => {
      setDetailData((prev) => {
        const list = prev[parentId] ?? [];
        const nextRoomId = Number(room?.id ?? 0);
        if (!Number.isFinite(nextRoomId) || nextRoomId <= 0) {
          return prev;
        }
        const idx = list.findIndex((r) => Number(r?.id) === nextRoomId);
        const nextList =
          idx >= 0
            ? list.map((r, i) => (i === idx ? { ...r, ...room } : r))
            : [...list, room];
        return { ...prev, [parentId]: nextList };
      });
    },
    []
  );

  const handleExpand = (expanded: boolean, record: AccommodationProduct) => {
    const id = Number(record.id);
    if (expanded && !detailData[id]) {
      fetchRooms(id);
    }
  };

  const openAccommodationRoomEditor = React.useCallback(
    (parentId: number, room: AccommodationRoom) => {
      const parentKey = String(parentId);
      setExpandedRowKeys((prev) => (prev.includes(parentKey) ? prev : [...prev, parentKey]));
      setDetailData((prev) => {
        const list = prev[parentId] ?? [];
        const roomId = Number(room.id);
        const idx = list.findIndex((r) => Number(r.id) === roomId);
        const nextList = idx >= 0 ? list.map((r, i) => (i === idx ? { ...r, ...room } : r)) : [...list, room];
        return { ...prev, [parentId]: nextList };
      });
      setCurrentParentId(parentId);
      setNewRoom(room);
      setIsEditRoom(true);
      setAddRoomModalOpen(true);
    },
    []
  );

  const openAccommodationRoomByProductId = React.useCallback(
    async (roomProductId: number) => {
      try {
        const roomDto = await api.getAccommodationRoomById(roomProductId.toString(), DivisionId());
        const parentId = Number(roomDto?.accommodationId ?? roomDto?.AccommodationId);
        if (!Number.isFinite(parentId) || parentId <= 0) {
          return false;
        }

        const res: any = await api.getAccommodationRoomsByAccommodationId(parentId.toString());
        const rooms: any[] = Array.isArray(res) ? res : (res?.items || res?.data || []);
        const mapped = rooms.map(mapApiRoomToAccommodationRoom);
        const matched =
          mapped.find((r) => Number(r.id) === roomProductId) ??
          mapApiRoomToAccommodationRoom({
            ...roomDto,
            productId: roomProductId,
            ProductId: roomProductId,
          });

        openAccommodationRoomEditor(parentId, matched);
        return true;
      } catch {
        return false;
      }
    },
    [openAccommodationRoomEditor]
  );

  const openAccommodationByProductId = React.useCallback(
    async (productId: number, name = "") => {
      const list = accommodationProducts ?? [];
      const directMatch = list.find((p) => Number(p.id) === productId);
      if (directMatch) {
        getAccommodationProduct(Number(directMatch.id), name || directMatch.name);
        return;
      }

      const openedRoom = await openAccommodationRoomByProductId(productId);
      if (openedRoom) {
        return;
      }

      getAccommodationProduct(productId, name);
    },
    [accommodationProducts, openAccommodationRoomByProductId]
  );

  useAutoOpenProductModal("Accommodation", (id) => {
    void openAccommodationByProductId(id);
  });

  const data = (filteredData ?? productList) as AccommodationProduct[];
  const sortedData = useMemo(() => {
    return sortProductsWithStatus(data, sortKey, sortOrder, statusFilter);
  }, [data, sortKey, sortOrder, statusFilter]);

  const pagedData = useMemo(
    () => TablePagination.dataPaging(sortedData, currentPage, itemsPerPage),
    [sortedData, currentPage, itemsPerPage]
  );
  const pagedIds = useMemo(() => pagedData.map(r => r.id.toString()), [pagedData]);
  const totalItems = filteredData ? filteredData.length : productList.length;
  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  useEffect(() => {
    const wanted = expandAll ? pagedIds : [];
    if (!arraysEqual(expandedRowKeys, wanted)) {
      const newKeys = wanted.filter(k => !expandedRowKeys.includes(k));
      newKeys.forEach(k => fetchRooms(Number(k)));
      setExpandedRowKeys(wanted);
    }
  }, [expandAll, pagedIds]);

  const hasPendingRooms = useMemo(() => pendingRoomIds.size > 0, [pendingRoomIds]);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col flex={2}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TableSearch
                disabled={showFilters}
                dataList={productList}
                onSearch={(list: Product[], searchActive = false) => {
                  setCurrentPage(1);
                  setIsSearchActive(searchActive);
                  setSearchFilteredData(list as AccommodationProduct[]);
                  publishFilteredData(filters, showFilters, statusFilter, searchActive, list as AccommodationProduct[]);
                }}
              />
            </div>
            <ProductStatusFilter
              value={statusFilter}
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setCurrentPage(1);
                publishFilteredData(filters, showFilters, nextStatus, isSearchActive, searchFilteredData);
              }}
            />
          </div>
        </Col>
        <Col style={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={expandAll}
            onChange={(checked) => {
              setExpandAll(checked);
            }}
            checkedChildren="Collapse All"
            unCheckedChildren="Expand All"
            style={{ marginRight: 12 }}
          />
        </Col>
        <Col flex={3} style={{ textAlign: 'right' }}>
          <Space>
            <StatusIcon
              status={status}
              lastEventReceived={lastEventReceived}
              unpublishedChanges={
                (Array.isArray(sortedData) && sortedData.some((x) => x?.entityUpdate === true)) ||
                hasPendingRooms
              }
              onManualRefresh={refreshGridEvent}
            />
            <TableActionButtons
              showFilters={showFilters}
              dataList={filteredData ?? productList}
              onShowFiltersClick={() => {
                const nextShowFilters = !showFilters;
                setShowFilters(nextShowFilters);
                if (nextShowFilters) {
                  setFilters([]);
                  publishFilteredData([], true, statusFilter, isSearchActive, searchFilteredData);
                } else {
                  publishFilteredData(filters, false, statusFilter, isSearchActive, searchFilteredData);
                }
                setCurrentPage(1);
              }}
              canFilter canExport canEdit={false} canAdd={false}
              csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_Accommodations_Export`}
            />
            <Button
              type="primary"
              onClick={openNewAccommodation}
              icon={<PlusOutlined />}
            >
              Add a new Accommodation
            </Button>
          </Space>
        </Col>
      </Row>

      {(() => {
        if (!showFilters) return null;
        return (
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col>
              <Input
                placeholder="Search..."
                value={filterHelper.getFieldValue(filters, 'name', '')}
                onChange={(e: { target: { value: any } }) => handleFilterChange(e.target.value, 'name')}
                allowClear
                style={{ width: 140 }}
              />
            </Col>
            <Col>
              <Select
                mode="multiple"
                allowClear
                placeholder="Select type"
                value={filterHelper.getFieldValue(filters, 'type')}
                onChange={(vals: any) => handleFilterChange(vals, 'type')}
                options={getUniqueTypeFilter(productList)}
                style={{ width: 130 }}
              />
            </Col>
            <Col>
              <Select
                mode="multiple"
                allowClear
                placeholder="Min Stay"
                value={filterHelper.getFieldValue(filters, 'minStay')}
                onChange={(vals: any) => handleFilterChange(vals, 'minStay')}
                options={getUniqueMinStayFilter(productList)}
                style={{ width: 110 }}
              />
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <FilterResetLink onClick={handleClearFilters} />
            </Col>
          </Row>
        );
      })()}

      <Table
        components={{
          body: {
            row: (({ index, ...props }: any) => {
              const [form] = Form.useForm();
              return (
                <Form form={form} component={false}>
                  <tr {...props} />
                </Form>
              );
            }) as React.FC<any>,
            cell: (({
              title, editable, children, dataIndex,
              record, inputType = 'text', options, ...restProps
            }: any) => {
              const form = useContext(React.createContext<any>(null))!;
              useEffect(() => {
                if (editable) {
                  form?.setFieldsValue?.({ [dataIndex]: record[dataIndex] });
                }
              }, [record, form, dataIndex]);
              let inputNode: React.ReactNode;
              switch (inputType) {
                case 'select':
                  inputNode = <Select options={options} placeholder={`Select ${String(title).toLowerCase()}`} allowClear />;
                  break;
                case 'checkbox':
                  inputNode = <Checkbox />;
                  break;
                case 'date':
                  inputNode = <DatePicker format={DateHelper.DATE_PICKER_FORMATS_ISO} style={{ width: '100%' }} />;
                  break;
                default:
                  inputNode = <Input />;
              }
              return (
                <td {...restProps}>
                  {editable ? (
                    <Form.Item
                      name={dataIndex}
                      style={{ margin: 0 }}
                      rules={dataIndex !== 'decommissionDate' ? [{ required: true, message: `${title} is required.` }] : []}
                    >
                      {inputNode}
                    </Form.Item>
                  ) : (children)}
                </td>
              );
            }) as React.FC<any>
          }
        }}
  columns={useMemo(() => [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (t: any, r: AccommodationProduct) => (
        <>
          {t}
          {r.entityUpdate && <LockOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />}
        </>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSortProps('name'),
      render: (text: string, record: AccommodationProduct) =>
        record.entityUpdate ? (
          <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
        ) : (
          <a
            href="#0"
            onClick={e => {
              e.preventDefault();
              getAccommodationProduct(Number(record.id), record.name);
            }}
          >
            {text}
            {Number(record.roomCount ?? 0) > 0 ? ` (${Number(record.roomCount)} products)` : ''}
          </a>
        )
    },
    {
      title: 'Accommodation Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      filters: getUniqueTypeFilter(productList),
      onFilter: (v: any, r: AccommodationProduct) => r.type === v
    },
    {
      title: 'Min Stay (Weeks)',
      dataIndex: 'minStay',
      key: 'minStay',
      width: 130,
      filters: getUniqueMinStayFilter(productList),
      onFilter: (v: any, r: AccommodationProduct) => r.minStay === v
    },
    {
      title: 'Is Committed',
      dataIndex: 'isCommitted',
      key: 'isCommitted',
      width: 120,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      onFilter: (v: any, r: AccommodationProduct) => r.isCommitted === v,
      render: (val: boolean) => (val ? 'YES' : 'NO')
    },
    {
      title: 'Is Non-Committed',
      dataIndex: 'isNonCommitted',
      key: 'isNonCommitted',
      width: 140,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      onFilter: (v: any, r: AccommodationProduct) => r.isNonCommitted === v,
      render: (val: boolean) => (val ? 'YES' : 'NO')
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 160,
      ...getColumnSortProps('lastModifiedDate'),
      render: (val: Date) => DateHelper.ToDateString(val)
    },
    {
      title: 'Last Updated By',
      dataIndex: 'lastUpdatedBy',
      key: 'lastUpdatedBy',
      width: 150,
      ...getColumnSortProps('lastUpdatedBy'),
    },
   {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (val: boolean) => val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>
  },
  ], [productList, getColumnSortProps])}
        dataSource={pagedData}
        rowKey={(r: AccommodationProduct) => String(r.id)}
        pagination={false}
        bordered
        size="middle"
        showSorterTooltip={false}
        onChange={(_, __, sorter: any, extra: any) => {
          handleSorterChange(sorter, extra?.action);
        }}
        expandable={{
        expandedRowRender: (record: AccommodationProduct) => {
          const parentId = record.id;
          const parentLocked = record.entityUpdate;
          const childCols: ColumnType<AccommodationRoom>[] = [
            {
              title: 'Name',
              dataIndex: 'ProductName',
              key: 'ProductName',
              render: (text: string, row: AccommodationRoom) => {
                const roomLocked =
                  parentLocked ||
                  row.entityUpdate ||
                  pendingRoomIds.has(Number((row as any)?.id ?? 0));
                if (roomLocked) {
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {(parentLocked || row.entityUpdate || pendingRoomIds.has(Number((row as any)?.id ?? 0))) && (
                        <LockOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                      )}
                      <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
                    </span>
                  );
                }
                return (
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      setCurrentParentId(Number(parentId));
                      setNewRoom(row);
                      setIsEditRoom(true);
                      setAddRoomModalOpen(true);
                    }}
                  >
                    {text}
                  </a>
                );
              }
            },
            {
              title: 'Room Grade',
              dataIndex: 'roomGradeId',
              key: 'roomGradeId',
              render: (id?: number, r?: AccommodationRoom) =>
                r?.roomGradeText || (id ? roomGradeMap.get(id) : '')
            },
            {
              title: 'Bathroom Type',
              dataIndex: 'bathroomTypeId',
              key: 'bathroomTypeId',
              render: (id?: number, r?: AccommodationRoom) =>
                r?.bathroomTypeText || (id ? bathroomTypeMap.get(id) : '')
            },
            {
              title: 'Board Basis',
              dataIndex: 'boardBasisId',
              key: 'boardBasisId',
              render: (id?: number, r?: AccommodationRoom) =>
                r?.boardBasisText || (id ? boardBasisMap.get(id) : '')
            },
            {
              title: 'Room Type',
              dataIndex: 'roomTypeId',
              key: 'roomTypeId',
              render: (id?: number, r?: AccommodationRoom) =>
                r?.roomTypeText || (id ? roomTypeMap.get(id) : '')
            },
            {
              title: 'Product Category',
              dataIndex: 'productCategoryId',
              key: 'productCategoryId',
              render: (id?: number, r?: AccommodationRoom) =>
                r?.productCategoryText || (id ? productCategoryMap.get(id) : '')
            },
            {
              title: 'Account Category',
              dataIndex: 'accountCategoryId',
              key: 'accountCategoryId',
              render: (id?: number, r?: AccommodationRoom) =>
                r?.accountCategoryText || (id ? accountCategoryMap.get(id) : '')
            },
            {
              title: 'Occupy Room',
              dataIndex: 'occupyRoom',
              key: 'occupyRoom',
              render: (val: boolean) => <Checkbox checked={val} disabled />
            },
            {
                title: 'Active',
                dataIndex: 'active',
                key: 'active',
                width: 100,
                render: (val: boolean) => val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>
            }
          ];

        return (
          <div>
            {!parentLocked && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  setCurrentParentId(Number(parentId));
                  setNewRoom({ ProductName: record.name ?? '', active: true });
                  setIsEditRoom(false);
                  setAddRoomModalOpen(true);
                }}
                style={{ marginBottom: 16 }}
              >
                Add Room
              </Button>
            )}
            {parentLocked && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16, color: 'rgba(0,0,0,0.65)', fontSize: 12 }}>
                <LockOutlined style={{ color: '#8c8c8c' }} />
                (Publishing an update) - room edits disabled
              </span>
            )}
            <Table
              rowClassName="editable-row"
              bordered
              dataSource={detailData[Number(parentId)] || []}
              columns={childCols}
              rowKey={(r) => r.id.toString()}
              pagination={false}
            />
          </div>
        );
      },
  rowExpandable: () => true,
   expandedRowKeys, 
    onExpandedRowsChange: (keys) => {
      const k = (keys as React.Key[]).map(String);
        const newKeys = k.filter(key => !expandedRowKeys.includes(key));
        newKeys.forEach(key => fetchRooms(Number(key)));
    if (!arraysEqual(expandedRowKeys, k)) setExpandedRowKeys(k);
     const allOnPage = k.length === pagedIds.length && pagedIds.length > 0;
    if (expandAll !== allOnPage) setExpandAll(allOnPage);
    },
    onExpand: handleExpand,
    expandRowByClick: true,
}}
        className="main row"
        style={{ marginBottom: 12 }}
        rowClassName={(record: AccommodationProduct) =>
          Number(record.roomCount ?? 0) === 0 ? 'acc-row-no-rooms' : ''
        }
      />

      <TablePagination.Paging
        noOfItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(val, page) => { setItemsPerPage(val); setCurrentPage(page); }}
        onPageChange={page => setCurrentPage(page)}
        showItemsPerPage
      />

      <Modal
        title={modalAccId ? 'Update Accommodation' : 'Add Accommodation'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); onCancel(); }}
        footer={null}
        width={980}
        style={{ top: 50 }}
        destroyOnHidden
      >
        <AddEditAccommodationForm onClose={() => { setModalOpen(false); onCancel(); }} />
      </Modal>

     <Modal
        title={isEditRoom ? "Edit Room" : "Add New Room"}
        open={addRoomModalOpen}
        onCancel={() => { if (!savingRoom) setAddRoomModalOpen(false); }}
        okButtonProps={{ loading: savingRoom, disabled: savingRoom }}
        onOk={async () => {
          if (!newRoom.ProductName || !currentParentId) {
            message.warning("Please enter a room name.");
            return;
          }
          if (!newRoom.roomGradeId || Number(newRoom.roomGradeId) <= 0) {
            message.warning("Please select a Room Grade.");
            return;
          }
          if (!newRoom.bathroomTypeId || Number(newRoom.bathroomTypeId) <= 0) {
            message.warning("Please select a Bathroom Type.");
            return;
          }
          if (!newRoom.roomTypeId || Number(newRoom.roomTypeId) <= 0) {
            message.warning("Please select a Room Type.");
            return;
          }
          if (!newRoom.productCategoryId || Number(newRoom.productCategoryId) <= 0) {
            message.warning("Please select a Product Category.");
            return;
          }
          if (!newRoom.accountCategoryId || Number(newRoom.accountCategoryId) <= 0) {
            message.warning("Please select an Account Category.");
            return;
          }
          if (!newRoom.boardBasisId || newRoom.boardBasisId <= 0) {
            message.warning("Please select a Board Basis.");
            return;
          }

          try {
            setSavingRoom(true);

            const toIntOrUndef = (v: unknown) =>
              v === undefined || v === null || v === '' ? undefined : Number(v);

            const roomIdValue = isEditRoom && newRoom.id ? Number(newRoom.id) : (newRoom.productId ? Number(newRoom.productId) : 0);
            const productId = roomIdValue > 0 ? roomIdValue : 0;
            
            const rawPayload: any = {
              ProductId: isEditRoom ? Number(newRoom.id) || 0 : 0,
              AccommodationId: Number(currentParentId),
              ProductName: String(newRoom.ProductName ?? '').trim(),

              roomGradeId: toIntOrUndef(newRoom.roomGradeId),
              bathroomTypeId: toIntOrUndef(newRoom.bathroomTypeId),
              boardTypeId: Number(newRoom.boardBasisId),
              roomTypeId: toIntOrUndef(newRoom.roomTypeId),

              productCategoryId: toIntOrUndef(newRoom.productCategoryId),
              accountCategoryId: toIntOrUndef(newRoom.accountCategoryId),

              occupyRoom: !!newRoom.occupyRoom,
              active: newRoom.active ?? true,

              offeringClosureDate: newRoom.decommissionDate
                ? (newRoom.decommissionDate as dayjs.Dayjs).format('YYYY-MM-DD')
                : undefined,
            };

            const payload = Object.fromEntries(
              Object.entries(rawPayload).filter(([, v]) => v !== undefined)
            );

            // Save
            const { saveAccommodationRoom } = await import('app/shared/api/saveAccommodationRoom');
            const result = await saveAccommodationRoom(payload, DivisionId());

          if (!result.ok) {
              if (result.errors && result.errors.length > 0) {
                message.error(result.errors.join('; '));
              } else if (result.message) {
                message.error(result.message);
              } else {
                message.error('Failed to save room.');
              }
              return;
            }

            message.success(isEditRoom ? 'Room updated.' : 'Room added.');

            // Backend returns the created room. Add it to UI immediately,
            // locked until publishing completes (entityUpdate=true).
            const created = (result as any)?.data ?? null;
            const createdId = Number(
              created?.productId ??
                created?.ProductId ??
                created?.id ??
                created?.Id ??
                created?.roomId ??
                created?.RoomId ??
                0
            );
            const createdName = String(
              created?.productName ??
                created?.ProductName ??
                created?.name ??
                created?.Name ??
                newRoom.ProductName ??
                ''
            ).trim();

            if (Number.isFinite(createdId) && createdId > 0) {
              setPendingRoomIds((prev) => {
                const next = new Set(prev);
                next.add(createdId);
                return next;
              });
              upsertRoomInState(Number(currentParentId), {
                ...(created as any),
                id: createdId,
                ProductName: createdName,
                roomGradeId: toNum(created?.RoomGradeId ?? created?.roomGradeId ?? newRoom.roomGradeId),
                bathroomTypeId: toNum(created?.BathRoomTypeId ?? created?.bathroomTypeId ?? created?.BathroomTypeId ?? newRoom.bathroomTypeId),
                boardBasisId: toNum(created?.BoardTypeId ?? created?.boardBasisId ?? created?.boardBasisId ?? newRoom.boardBasisId),
                roomTypeId: toNum(created?.RoomTypeId ?? created?.roomTypeId ?? newRoom.roomTypeId),
                productCategoryId: toNum(created?.ProductCategoryId ?? created?.productCategoryId ?? newRoom.productCategoryId),
                accountCategoryId: toNum(created?.AccountCategoryId ?? created?.accountCategoryId ?? newRoom.accountCategoryId),
                decommissionDate: toDay(created?.OfferingsClosureDate ?? created?.decommissionDate ?? newRoom.decommissionDate),
                // Force lock until publish completes.
                entityUpdate: true,
                active: created?.active ?? newRoom.active ?? true,
              });
            } else {
              // Fallback: reload rooms list.
              await fetchRooms(currentParentId!);
            }

          setAddRoomModalOpen(false);
          setIsEditRoom(false);
          setNewRoom({});
          } catch (err) {
            console.error(err);
            message.error('Unexpected error while saving.');
          } finally {
            setSavingRoom(false);
        }
      }}

        okText={isEditRoom ? "Save" : "Add"}
        width={700}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Name">
                <Input
                  value={newRoom.ProductName}
                  onChange={e => setNewRoom(r => ({ ...r, ProductName: e.target.value }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Room Grade" required>
                <Select
                  options={roomGradeOptions}
                  value={newRoom.roomGradeId ?? undefined}
                  onChange={val => setNewRoom(r => ({ ...r, roomGradeId: val }))}
                  placeholder="Select room grade"
                  showSearch={{ optionFilterProp: 'label' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
             <Form.Item label="Bathroom Type" required>
                <Select
                  options={bathroomTypeOptions}
                  value={newRoom.bathroomTypeId ?? undefined}
                  onChange={val => setNewRoom(r => ({ ...r, bathroomTypeId: val }))}
                  placeholder="Select bathroom type"
                  showSearch={{ optionFilterProp: 'label' }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
               <Form.Item label="Board Basis">
                <Select
                  options={boardBasisOptions}
                  value={newRoom.boardBasisId ?? undefined}
                  onChange={val => setNewRoom(r => ({ ...r, boardBasisId: val }))}
                  placeholder="Select board basis"
                  showSearch={{ optionFilterProp: 'label' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Room Type" required>
                <Select
                  options={roomTypeOptions}
                  value={newRoom.roomTypeId ?? undefined}
                  onChange={val => setNewRoom(r => ({ ...r, roomTypeId: val }))}
                  placeholder="Select room type"
                  showSearch={{ optionFilterProp: 'label' }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Product Category" required>
                <Select
                  options={productCategoryOptions}
                  value={newRoom.productCategoryId ?? undefined}
                  onChange={val => setNewRoom(r => ({ ...r, productCategoryId: val }))}
                  placeholder="Select product category"
                  showSearch={{ optionFilterProp: 'label' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            
            <Col span={12}>
             <Form.Item label="Account Category" required>
                <Select
                  options={accountCategoryOptions}
                  value={newRoom.accountCategoryId ?? undefined}
                onChange={val => setNewRoom(r => ({ ...r, accountCategoryId: val }))}
                  placeholder="Select account category"
                  showSearch={{ optionFilterProp: 'label' }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}></Col>

            <Col span={12} style={{ display: 'flex', alignItems: 'center', marginTop: 30 }}>
              
            <Form.Item label="Occupy Room" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch
                checked={!!newRoom.occupyRoom}
                onChange={checked => setNewRoom(r => ({ ...r, occupyRoom: checked }))}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center', marginTop: 30 }}>
              <Form.Item label="Active" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch
                  checked={!!newRoom.active}
                  onChange={checked => setNewRoom(r => ({ ...r, active: checked }))}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}

export default AccommodationGrid;